import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const exportComparisonToPDF = (platforms, answers) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Title
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('AI Platform Comparison', pageWidth / 2, 20, { align: 'center' });
  
  // Subtitle with date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated on ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 28, { align: 'center' });
  doc.text('GoCompareAI - https://gocompareai.com', pageWidth / 2, 33, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  let yPos = 45;
  
  // Platform Overview Table
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Platform Overview', 14, yPos);
  yPos += 8;
  
  const overviewData = platforms.map(p => [
    p.name,
    p.provider,
    p.suitability === 'suitable' ? 'Suitable' : p.suitability === 'caution' ? 'Caution' : 'Not Recommended',
    getCostLabel(p.costBand),
    p.pricing?.hasFreeeTier ? 'Yes' : 'No'
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Platform', 'Provider', 'Suitability', 'Cost', 'Free Tier']],
    body: overviewData,
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246], fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Features Comparison
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Feature Comparison', 14, yPos);
  yPos += 8;
  
  const features = [
    ['Conversational AI', ...platforms.map(p => p.features?.conversational ? 'Yes' : 'No')],
    ['Code Generation', ...platforms.map(p => p.features?.codeGeneration ? 'Yes' : 'No')],
    ['Image Generation', ...platforms.map(p => p.features?.imageGeneration ? 'Yes' : 'No')],
    ['Image Analysis', ...platforms.map(p => p.features?.imageAnalysis ? 'Yes' : 'No')],
    ['Document Analysis', ...platforms.map(p => p.features?.documentAnalysis ? 'Yes' : 'No')],
    ['Web Browsing', ...platforms.map(p => p.features?.webBrowsing ? 'Yes' : 'No')],
    ['API Access', ...platforms.map(p => p.features?.apiAccess ? 'Yes' : 'No')],
    ['Mobile App', ...platforms.map(p => p.features?.mobileApp ? 'Yes' : 'No')],
    ['Team Collaboration', ...platforms.map(p => p.features?.teamCollaboration ? 'Yes' : 'No')],
  ];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Feature', ...platforms.map(p => p.name)]],
    body: features,
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246], fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
    didParseCell: function(data) {
      // Color code Yes/No cells
      if (data.section === 'body' && data.column.index > 0) {
        if (data.cell.raw === 'Yes') {
          data.cell.styles.textColor = [34, 197, 94]; // Green
          data.cell.styles.fontStyle = 'bold';
        } else if (data.cell.raw === 'No') {
          data.cell.styles.textColor = [156, 163, 175]; // Gray
        }
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Check if we need a new page
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }
  
  // Data & Security
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Data & Security', 14, yPos);
  yPos += 8;
  
  const security = [
    ['Data Location', ...platforms.map(p => p.dataHandling?.dataLocation || 'N/A')],
    ['GDPR Compliant', ...platforms.map(p => p.dataHandling?.gdprCompliant ? 'Yes' : 'No')],
    ['HIPAA Compliant', ...platforms.map(p => p.dataHandling?.hipaaCompliant ? 'Yes' : 'No')],
    ['SOC 2 Certified', ...platforms.map(p => p.dataHandling?.soc2Certified ? 'Yes' : 'No')],
    ['Opt-out Available', ...platforms.map(p => p.dataHandling?.optOutAvailable ? 'Yes' : 'No')],
  ];
  
  autoTable(doc, {
    startY: yPos,
    head: [['Security', ...platforms.map(p => p.name)]],
    body: security,
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246], fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
    didParseCell: function(data) {
      // Color code Yes/No cells (skip first row which is Data Location)
      if (data.section === 'body' && data.column.index > 0 && data.row.index > 0) {
        if (data.cell.raw === 'Yes') {
          data.cell.styles.textColor = [34, 197, 94]; // Green
          data.cell.styles.fontStyle = 'bold';
        } else if (data.cell.raw === 'No') {
          data.cell.styles.textColor = [156, 163, 175]; // Gray
        }
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 15;
  
  // Check if we need a new page
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }
  
  // Pricing Plans
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Pricing Plans', 14, yPos);
  yPos += 8;
  
  const pricingData = [];
  platforms.forEach(p => {
    if (p.pricing?.paidPlans) {
      p.pricing.paidPlans.forEach((plan, index) => {
        pricingData.push([
          index === 0 ? p.name : '',
          plan.name,
          plan.price,
          plan.features
        ]);
      });
    }
  });
  
  if (pricingData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Platform', 'Plan', 'Price', 'Features']],
      body: pricingData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold' },
        3: { cellWidth: 60 }
      }
    });
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Disclaimer
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.setTextColor(128, 128, 128);
  const disclaimer = 'Disclaimer: This comparison is for informational purposes only. Information is sourced from vendor documentation and independent research. Always verify current details directly with providers before making decisions.';
  const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 28);
  doc.text(splitDisclaimer, 14, yPos);
  
  // Save the PDF
  const filename = `ai-comparison-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
  
  return filename;
};

function getCostLabel(costBand) {
  const labels = ['Free', '£', '££', '£££', '££££'];
  return labels[costBand - 1] || '—';
}

export const exportResultsToPDF = (platforms, answers) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Title
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('AI Platform Recommendations', pageWidth / 2, 20, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated on ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 28, { align: 'center' });
  doc.text('GoCompareAI - Personalised recommendations based on your needs', pageWidth / 2, 33, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  let yPos = 45;
  
  // Your Requirements Summary
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Your Requirements', 14, yPos);
  yPos += 8;
  
  const requirementsData = [];
  if (answers.purpose?.length) {
    requirementsData.push(['Purpose', answers.purpose.join(', ')]);
  }
  if (answers.userType) {
    requirementsData.push(['User Type', answers.userType]);
  }
  if (answers.dataSensitivity) {
    requirementsData.push(['Data Sensitivity', answers.dataSensitivity]);
  }
  if (answers.technicalLevel) {
    requirementsData.push(['Technical Level', answers.technicalLevel]);
  }
  if (answers.budget) {
    requirementsData.push(['Budget', answers.budget]);
  }
  
  if (requirementsData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      head: [['Criteria', 'Your Selection']],
      body: requirementsData,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94], fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
    });
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Recommendations
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Recommended Platforms', 14, yPos);
  yPos += 8;
  
  platforms.forEach((platform, index) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    // Platform header
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    const suitabilityIcon = platform.suitability === 'suitable' ? '✓' : platform.suitability === 'caution' ? '⚠' : '✗';
    doc.text(`${index + 1}. ${platform.name} (${platform.provider}) ${suitabilityIcon}`, 14, yPos);
    yPos += 6;
    
    // Suitability
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const suitabilityText = platform.suitability === 'suitable' ? 'Suitable' : platform.suitability === 'caution' ? 'Partially suitable' : 'Not recommended';
    doc.text(`Suitability: ${suitabilityText} (${Math.round(platform.percentage || 0)}% match)`, 14, yPos);
    yPos += 5;
    
    // Summary
    doc.setFontSize(9);
    const summaryLines = doc.splitTextToSize(platform.summary || '', pageWidth - 28);
    doc.text(summaryLines, 14, yPos);
    yPos += summaryLines.length * 4 + 3;
    
    // Reasons
    if (platform.reasons?.suitable?.length > 0) {
      doc.setTextColor(34, 197, 94);
      doc.text('+ ' + platform.reasons.suitable.slice(0, 2).join(' | '), 14, yPos);
      yPos += 5;
    }
    
    if (platform.reasons?.concerns?.length > 0) {
      doc.setTextColor(234, 179, 8);
      doc.text('- ' + platform.reasons.concerns.slice(0, 2).join(' | '), 14, yPos);
      yPos += 5;
    }
    
    doc.setTextColor(0, 0, 0);
    yPos += 8;
  });
  
  // Disclaimer
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.setTextColor(128, 128, 128);
  const disclaimer = 'Disclaimer: These recommendations are based on your stated requirements and our research. They do not constitute professional advice. Always verify current platform details with providers.';
  const splitDisclaimer = doc.splitTextToSize(disclaimer, pageWidth - 28);
  doc.text(splitDisclaimer, 14, yPos);
  
  // Save
  const filename = `ai-recommendations-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
  
  return filename;
};
